import { promises as fs } from "fs";
import type { Tweet } from "@podx/core/types";
import { TokenExtractor, SentimentAnalyzer, BotDetector, SignalGenerator } from "../analyzers/index";
import type { TokenMention } from "../analyzers/TokenExtractor";
import type { AccountProfile } from "../analyzers/TokenExtractor";
import type { CryptoSignal } from "../analyzers/SignalGenerator";

// Re-export types for backward compatibility
export type { TokenMention, AccountProfile, CryptoSignal };

export class CryptoAnalyzer {
  private tweets: Tweet[] = [];
  private tokenExtractor = new TokenExtractor();
  private sentimentAnalyzer = new SentimentAnalyzer();
  private botDetector = new BotDetector();
  private signalGenerator = new SignalGenerator();

  // Solana contract address patterns
  private solanaContractPatterns = [
    // Base58 Solana addresses (32 bytes = 44 characters in base58)
    /\b[1-9A-HJ-NP-Za-km-z]{43,44}\b/g,
    // Common Solana token formats with specific validation
    /\b[A-Za-z0-9]{32,44}\b/g,
  ];

  // Known Solana program addresses and patterns for validation
  private knownSolanaPrograms = [
    '11111111111111111111111111111112', // System Program
    'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA', // Token Program
    'So11111111111111111111111111111111111111112', // Wrapped SOL
    'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v', // USDC
    '9n4nbM75f5Ui33ZbPYXn59EwSgE8CGsHtAeTH5YFeJ9E', // BTC (Wormhole)
    'pumpCmXqMfrsAkQ5r49WcJnRayYRqmXz6ae8H7H9Dfn', // PUMP token
  ];

  // Solana-specific keywords that help validate contracts
  private solanaTriggerWords = ['pump.fun', 'jupiter', 'raydium', 'orca', 'dexscreener', 'birdeye', 'solscan', 'phantom', 'solflare'];

  async loadTweets(filePath: string = 'scraped_tweets.json'): Promise<void> {
    const data = await fs.readFile(filePath, 'utf-8');
    this.tweets = JSON.parse(data);
    console.log(`Loaded ${this.tweets.length} tweets for analysis`);
  }

  getTweets(): Tweet[] {
    return this.tweets;
  }

  extractTokenMentions(): TokenMention[] {
    const mentions = this.tokenExtractor.extractTokenMentions(this.tweets);
    const accountMap = new Map<string, AccountProfile>();

    // Rebuild account map for bot detection
    for (const tweet of this.tweets) {
      const username = tweet.username || 'unknown';
      if (!accountMap.has(username)) {
        accountMap.set(username, {
          username,
          tweetCount: 0,
          tokensPosted: [],
          avgEngagement: 0,
          botScore: 0,
          shillScore: 0,
          reputation: 'trusted'
        });
      }
      const account = accountMap.get(username)!;
      account.tweetCount++;
      const tokens = tweet.text.match(/\$([A-Z]{2,10}|[A-Z][A-Z0-9]{1,9})/g) || [];
      for (const token of tokens) {
        const cleanToken = token.toUpperCase();
        if (!account.tokensPosted.includes(cleanToken)) {
          account.tokensPosted.push(cleanToken);
        }
      }
    }

    // Analyze accounts for bot detection
    this.botDetector.analyzeAccounts(this.tweets, accountMap);

    // Enhance mentions with sentiment and bot analysis
    for (const mention of mentions) {
      mention.sentimentScore = this.sentimentAnalyzer.analyzeSentimentScore(mention.tweets);
      mention.sentiment = this.sentimentAnalyzer.scoreToSentiment(mention.sentimentScore);
      mention.botScore = this.botDetector.calculateTokenBotScore(mention.tweets, accountMap);
      mention.shillProbability = this.botDetector.calculateShillProbability(mention.tweets, accountMap, mention.symbol);
    }

    return mentions;
  }

  analyzeSentiment(tweets: Tweet[]): 'positive' | 'negative' | 'neutral' {
    return this.sentimentAnalyzer.analyzeSentiment(tweets);
  }

  analyzeSentimentScore(tweets: Tweet[]): number {
    return this.sentimentAnalyzer.analyzeSentimentScore(tweets);
  }

  scoreToSentiment(score: number): 'positive' | 'negative' | 'neutral' {
    return this.sentimentAnalyzer.scoreToSentiment(score);
  }

  // Extract and validate Solana contract addresses
  extractSolanaContracts(text: string): string[] {
    const contracts: string[] = [];
    const contractCandidates = new Set<string>();

    // Extract potential contract addresses using patterns
    for (const pattern of this.solanaContractPatterns) {
      const matches = text.match(pattern) || [];
      matches.forEach(match => contractCandidates.add(match));
    }

    // Validate and filter candidates
    for (const candidate of contractCandidates) {
      if (this.isValidSolanaContract(candidate, text)) {
        contracts.push(candidate);
      }
    }

    return contracts;
  }

  // Validate if a string is likely a valid Solana contract address
  private isValidSolanaContract(candidate: string, context: string): boolean {
    // Length validation (Solana addresses are typically 32-44 characters)
    if (candidate.length < 32 || candidate.length > 44) {
      return false;
    }

    // Check if it's a known Solana program
    if (this.knownSolanaPrograms.includes(candidate)) {
      return true;
    }

    // Base58 character validation (Solana uses base58)
    const base58Regex = /^[1-9A-HJ-NP-Za-km-z]+$/;
    if (!base58Regex.test(candidate)) {
      return false;
    }

    // Context validation - look for Solana-related keywords in the surrounding text
    const contextLower = context.toLowerCase();
    const hasSolanaContext = this.solanaTriggerWords.some(keyword => 
      contextLower.includes(keyword)
    );

    // Additional validation patterns
    const hasContractContext = /\b(contract|token|address|mint|ca:)\b/i.test(context);
    const hasURLContext = /\b(dexscreener|birdeye|solscan|pump\.fun)\b/i.test(context);
    
    // Must have some context indicators to be considered valid
    return hasSolanaContext || hasContractContext || hasURLContext;
  }

  // Analyze tweets for Solana contracts and return structured data
  analyzeSolanaContracts(): {
    totalContracts: number;
    uniqueContracts: string[];
    contractMentions: Array<{
      contract: string;
      mentions: number;
      tweets: Tweet[];
      firstSeen: Date;
      lastSeen: Date;
      contexts: string[];
    }>;
  } {
    const contractMap = new Map<string, Tweet[]>();
    const contextMap = new Map<string, string[]>();

    // Extract contracts from all tweets
    for (const tweet of this.tweets) {
      const contracts = this.extractSolanaContracts(tweet.text);
      
      for (const contract of contracts) {
        if (!contractMap.has(contract)) {
          contractMap.set(contract, []);
          contextMap.set(contract, []);
        }
        contractMap.get(contract)!.push(tweet);
        
        // Store context around the contract mention
        const contractIndex = tweet.text.indexOf(contract);
        if (contractIndex !== -1) {
          const contextStart = Math.max(0, contractIndex - 50);
          const contextEnd = Math.min(tweet.text.length, contractIndex + contract.length + 50);
          const context = tweet.text.substring(contextStart, contextEnd);
          contextMap.get(contract)!.push(context);
        }
      }
    }

    // Build structured response
    const contractMentions = Array.from(contractMap.entries()).map(([contract, tweets]) => {
      const sortedTweets = tweets.sort((a, b) => 
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );

      const first = sortedTweets[0];
      const last = sortedTweets[sortedTweets.length - 1];
      return {
        contract,
        mentions: tweets.length,
        tweets,
        firstSeen: new Date(first?.createdAt ?? 0),
        lastSeen: new Date(last?.createdAt ?? 0),
        contexts: contextMap.get(contract) || []
      };
    });

    // Sort by mention count
    contractMentions.sort((a, b) => b.mentions - a.mentions);

    return {
      totalContracts: contractMap.size,
      uniqueContracts: Array.from(contractMap.keys()),
      contractMentions
    };
  }

  analyzeAccounts(accountMap: Map<string, AccountProfile>): void {
    this.botDetector.analyzeAccounts(this.tweets, accountMap);
  }

  generateSignals(tokenMentions: TokenMention[]): CryptoSignal[] {
    return this.signalGenerator.generateSignals(tokenMentions);
  }

  getAccountProfiles(): AccountProfile[] {
    const accountMap = new Map<string, AccountProfile>();

    // Build account profiles from tweets
    for (const tweet of this.tweets) {
      const username = tweet.username || 'unknown';

      if (!accountMap.has(username)) {
        accountMap.set(username, {
          username,
          tweetCount: 0,
          tokensPosted: [],
          avgEngagement: 0,
          botScore: 0,
          shillScore: 0,
          reputation: 'trusted'
        });
      }

      const account = accountMap.get(username)!;
      account.tweetCount++;

      const tokens = tweet.text.match(/\$([A-Z]{2,10}|[A-Z][A-Z0-9]{1,9})/g) || [];
      for (const token of tokens) {
        const cleanToken = token.toUpperCase();
        if (!account.tokensPosted.includes(cleanToken)) {
          account.tokensPosted.push(cleanToken);
        }
      }
    }

    // Analyze accounts
    this.analyzeAccounts(accountMap);

    return Array.from(accountMap.values()).sort((a, b) => b.tweetCount - a.tweetCount);
  }

  async exportAnalysis(filename: string, saveToConvex = false): Promise<void> {
    const mentions = this.extractTokenMentions();
    const signals = this.generateSignals(mentions);
    const accounts = this.getAccountProfiles();

    const analysis = {
      generatedAt: new Date().toISOString(),
      totalTweets: this.tweets.length,
      totalAccounts: accounts.length,
      tokenMentions: mentions,
      signals,
      accountProfiles: accounts,
      summary: {
        topTokens: mentions.slice(0, 10).map(m => ({
          symbol: m.symbol,
          mentions: m.count,
          sentiment: m.sentiment,
          sentimentScore: m.sentimentScore,
          botScore: m.botScore,
          shillProbability: m.shillProbability
        })),
        signalDistribution: signals.reduce((acc, s) => {
          acc[s.signal] = (acc[s.signal] || 0) + 1;
          return acc;
        }, {} as Record<string, number>),
        accountReputation: accounts.reduce((acc, a) => {
          acc[a.reputation] = (acc[a.reputation] || 0) + 1;
          return acc;
        }, {} as Record<string, number>),
        topShillers: accounts
          .filter(a => a.shillScore > 0.5)
          .sort((a, b) => b.shillScore - a.shillScore)
          .slice(0, 5)
          .map(a => ({
            username: a.username,
            shillScore: a.shillScore,
            tokensPosted: a.tokensPosted
          }))
      }
    };

    // Save to file
    await fs.writeFile(filename, JSON.stringify(analysis, null, 2));
    console.log(`Enhanced analysis exported to ${filename}`);

    // Optionally save to Convex
    if (saveToConvex) {
      try {
        const { convexStorage } = await import('@podx/core');
        await convexStorage.saveAnalysis(analysis);
        console.log('✅ Analysis also saved to Convex database');
      } catch (error) {
        console.error('❌ Failed to save to Convex:', error);
      }
    }
  }

  async exportAccountAnalysis(filename: string): Promise<void> {
    const accounts = this.getAccountProfiles();

    const accountAnalysis = {
      generatedAt: new Date().toISOString(),
      totalAccounts: accounts.length,
      accounts: accounts,
      summary: {
        reputationBreakdown: accounts.reduce((acc, a) => {
          acc[a.reputation] = (acc[a.reputation] || 0) + 1;
          return acc;
        }, {} as Record<string, number>),
        topInfluencers: accounts
          .filter(a => a.reputation === 'trusted')
          .sort((a, b) => b.avgEngagement - a.avgEngagement)
          .slice(0, 10),
        suspectedBots: accounts
          .filter(a => a.botScore > 0.7)
          .sort((a, b) => b.botScore - a.botScore),
        shillers: accounts
          .filter(a => a.shillScore > 0.6)
          .sort((a, b) => b.shillScore - a.shillScore)
      }
    };

    await fs.writeFile(filename, JSON.stringify(accountAnalysis, null, 2));
    console.log(`Account analysis exported to ${filename}`);
  }
}
